'use client';

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type Dispatch,
	type PropsWithChildren,
	type SetStateAction,
} from 'react';

import { SubscriptionsSku } from '@calm-web/api-sdk/lib/atlas-subscriptions-api';

import { MainContentProvider } from '@/components/accessibility/MainContent';
import { usePageName } from '@/context/PageNameProvider';
import { type EligibilityStatus } from '@/data/atlasSubscriptionsApi';
import { useAnalytics } from '@/hooks/analytics';
import useUser from '@/hooks/auth/useUser';
import { useIsCanadaDevice, useIsGDPRDevice, useIsUKGDPRDevice } from '@/hooks/user';

import { type NonEmptySkuArray } from '../utils';

export type ScreenName = 'account' | 'payment' | 'handoff' | 'error';

type SisuContextProps = {
	// flow
	activeScreen: ScreenName;
	backgroundColor: string;
	flowName: string;
	isEligible: boolean;
	isIneligible: boolean;
	isAlreadySubscribed: boolean;
	isWithinModal?: boolean;
	logPageViewedEvent: (screen: ScreenName) => void;
	setActiveScreen: (screen: ScreenName) => void;

	// account
	isMarketingOptInSelected: boolean;
	setIsMarketingOptInSelected: Dispatch<SetStateAction<boolean>>;

	// payment
	getSkuByDuration: (duration: string) => SubscriptionsSku | undefined;
	hasFreeTrial: boolean;
	isSelectedSku: (sku: SubscriptionsSku) => boolean;
	selectedSku: SubscriptionsSku;
	setSelectedSku: Dispatch<SetStateAction<SubscriptionsSku>>;
	skus: NonEmptySkuArray;
};

export const SisuContext = createContext<SisuContextProps | undefined>(undefined);

export const useSisu = () => {
	const context = useContext(SisuContext);
	if (!context) {
		throw new Error('useSisu must be used within a SisuProvider');
	}
	return context;
};

export type ProviderProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * "gray0"
	 * ```
	 */
	backgroundColor?: string;

	/**
	 * Set this in a server component to control which screen shows first. Can be 'account', 'payment', 'handoff', or 'error'.
	 * @defaultValue
	 * ```tsx
	 * "account"
	 * ```
	 */
	defaultActiveScreen?: ScreenName;

	eligibilityStatus?: EligibilityStatus;

	flowName?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * false
	 * ```
	 */
	isWithinModal?: boolean;

	skus: NonEmptySkuArray;
};

export default function SisuProvider({
	children,

	backgroundColor = 'gray0',

	defaultActiveScreen = 'account',
	eligibilityStatus,
	flowName = 'Sisu',
	isWithinModal = false,
	skus,
}: PropsWithChildren<ProviderProps>) {
	const pageName = usePageName();
	const { logEvent } = useAnalytics();
	const { user } = useUser();

	const calmOneId: string | undefined = user?.['https://oneid.calm.com/calm_oneid'];

	// flow

	const isEligible = eligibilityStatus === 'eligible';
	const isIneligible = eligibilityStatus === 'ineligible';
	const isAlreadySubscribed = eligibilityStatus === 'already-subscribed';

	const [userNavigation, setUserNavigation] = useState<{ from: ScreenName; to: ScreenName } | null>(null);

	const setActiveScreen = useCallback(
		(screen: ScreenName) => setUserNavigation({ from: defaultActiveScreen, to: screen }),
		[defaultActiveScreen],
	);

	const baseScreen: ScreenName =
		userNavigation?.from === defaultActiveScreen ? userNavigation.to : defaultActiveScreen;

	const activeScreen: ScreenName = baseScreen === 'payment' && isAlreadySubscribed ? 'handoff' : baseScreen;

	const previousActiveScreenRef = useRef<ScreenName>(null);

	useEffect(() => {
		const previousActiveScreen = previousActiveScreenRef.current;

		if (previousActiveScreen === null) {
			previousActiveScreenRef.current = activeScreen;
			return;
		}

		if (previousActiveScreen !== activeScreen) {
			logEvent({
				eventName: 'Screen : Exited',
				eventProps: {
					screen_name: previousActiveScreen,
					exited_to: activeScreen,
					source: flowName,
				},
			});
			previousActiveScreenRef.current = activeScreen;
		}
	}, [activeScreen, logEvent, flowName]);

	const logPageViewedEvent = useCallback(
		(screen: ScreenName) => {
			logEvent({
				eventName: 'Page : Viewed',
				eventProps: {
					page_name: `${pageName}-${screen}`,
					source: flowName,
					...(calmOneId && { calm_user_id: calmOneId }),
				},
			});
		},
		[logEvent, pageName, calmOneId, flowName],
	);

	// account
	const isGDPRDevice = useIsGDPRDevice();
	const isUKGDPRDevice = useIsUKGDPRDevice();
	const isCanadaDevice = useIsCanadaDevice();
	const isMarketingOptInGated = isGDPRDevice || isUKGDPRDevice || isCanadaDevice;
	// This covers the gate already being known at mount. On pages with no server-preloaded
	// device state, though, geo resolves asynchronously - it can flip false -> true after this
	// first render - so we also need the render-time correction below to catch that case.
	const [isMarketingOptInSelected, setIsMarketingOptInSelected] = useState(!isMarketingOptInGated);
	// "Adjusting state when a prop changes" (not an effect): setting state here re-renders
	// before paint, so a gated user whose geo resolves late never sees a checked-box flash.
	const [prevIsMarketingOptInGated, setPrevIsMarketingOptInGated] = useState(isMarketingOptInGated);
	if (isMarketingOptInGated !== prevIsMarketingOptInGated) {
		setPrevIsMarketingOptInGated(isMarketingOptInGated);
		if (isMarketingOptInGated) {
			setIsMarketingOptInSelected(false);
		}
	}

	// payment
	const [defaultSelectedSku] = skus;
	const [selectedSku, setSelectedSku] = useState(defaultSelectedSku);

	const getSkuByDuration = useCallback(
		(duration: string) => skus.find(sku => sku.duration === duration),
		[skus],
	);

	const hasFreeTrial = !!selectedSku.trialDuration;

	const isSelectedSku = useCallback(
		(sku: SubscriptionsSku) => sku.rawSKU === selectedSku.rawSKU,
		[selectedSku],
	);

	const value = useMemo(
		() => ({
			// flow
			activeScreen,
			backgroundColor,
			flowName,
			isEligible,
			isIneligible,
			isAlreadySubscribed,
			isWithinModal,
			logPageViewedEvent,
			setActiveScreen,

			// account
			isMarketingOptInSelected,
			setIsMarketingOptInSelected,

			// payment
			getSkuByDuration,
			hasFreeTrial,
			isSelectedSku,
			selectedSku,
			setSelectedSku,
			skus,
		}),
		[
			// flow
			activeScreen,
			backgroundColor,
			flowName,
			isEligible,
			isIneligible,
			isAlreadySubscribed,
			isWithinModal,
			logPageViewedEvent,
			setActiveScreen,

			// account
			isMarketingOptInSelected,

			// payment
			getSkuByDuration,
			hasFreeTrial,
			isSelectedSku,
			selectedSku,
			skus,
		],
	);

	return (
		<SisuContext.Provider value={value}>
			{/* Inside a modal the page owns the main landmark, so the flow's PageWrap renders a div. */}
			{isWithinModal ? <MainContentProvider value>{children}</MainContentProvider> : children}
		</SisuContext.Provider>
	);
}
